import { money, type Money } from "@stb/core/money"

import { CENTS_PER_KM_CAP_KM } from "./cents-per-km.ts"
import { financialYearFromLabel } from "./financial-year.ts"

/** The ATO 5,000 km cap per car per income year, in metres (trip distances are stored in metres). */
export const EMPLOYER_REIMBURSEMENT_CAP_M = CENTS_PER_KM_CAP_KM * 1000

export interface EmployerReimbursement {
  /** Metres up to the 5,000 km cap. */
  withinCapDistanceM: number
  /** Metres past the cap. */
  overCapDistanceM: number
  withinCapAmount: Money
  overCapAmount: Money
  total: Money
}

/** `YYYY-YY` where the second part is the following year, e.g. `2026-27`. */
export function financialYearLabelIsValid(label: string): boolean {
  return financialYearFromLabel(label) !== null
}

/** Whole cents for `distanceM` metres at `rateCents` per km, halves rounded up; integers only. */
function amountCents(distanceM: number, rateCents: number): number {
  return Math.floor((distanceM * rateCents + 500) / 1000)
}

/**
 * What an employer pays for one employee-owned car over one income year when reimbursing at a
 * fixed cents per km rate: the first 5,000 km and the rest are reported apart, because the ATO
 * treats them differently for PAYG withholding. `distanceM` is the car's business metres for the
 * year and `rateCents` an integer cents per km; the rate itself is data (the employer's, or the
 * ATO table in `cents-per-km.ts`), never a constant here. The ATO cap is per car, so call this
 * once per car.
 */
export function splitEmployerReimbursement(
  distanceM: number,
  rateCents: number,
): EmployerReimbursement {
  if (!Number.isInteger(distanceM) || distanceM < 0) {
    throw new RangeError(`distanceM must be a non-negative whole number, got ${distanceM}`)
  }
  if (!Number.isInteger(rateCents) || rateCents < 1) {
    throw new RangeError(`rateCents must be a positive whole number, got ${rateCents}`)
  }
  const withinCapDistanceM = Math.min(distanceM, EMPLOYER_REIMBURSEMENT_CAP_M)
  const overCapDistanceM = distanceM - withinCapDistanceM
  const withinCapAmount = money(amountCents(withinCapDistanceM, rateCents), "AUD")
  const overCapAmount = money(amountCents(overCapDistanceM, rateCents), "AUD")
  return {
    withinCapDistanceM,
    overCapDistanceM,
    withinCapAmount,
    overCapAmount,
    total: money(withinCapAmount.amount + overCapAmount.amount, "AUD"),
  }
}
